import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';

import { BaseForecastModel } from '@app/common/static-data/models';

import { getModelNotes } from './modelNotes';

interface BenchmarkNotesProps {
  mode: 'absolute' | 'skill';
  model: BaseForecastModel;
}

// Define a reusable component for collapsible note sections
type NoteType = 'info' | 'skill' | 'missing';

interface NoteItemProps {
  title: string;
  type: NoteType;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function NoteItem({ title, expanded, onToggle, children }: NoteItemProps) {
  return (
    <div className="bg-white dark:bg-theme-dark-bg border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 text-left"
      >
        <div className="flex items-center">
          <Info className="h-4 w-4 text-gray-500 dark:text-gray-400 mr-2" />
          <span className="font-medium text-gray-700 dark:text-gray-300">{title}</span>
        </div>
        {expanded ? (
          <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400" />
        )}
      </button>

      {expanded && <div className="p-4 text-sm text-gray-600 dark:text-gray-400">{children}</div>}
    </div>
  );
}

export function BenchmarkNotes({ mode, model }: BenchmarkNotesProps) {
  const [expandedSections, setExpandedSections] = useState<NoteType[]>([]);

  // Get model-specific notes
  const modelNotes = getModelNotes(model.id);

  // Calculate whether we should show any notes
  const showSkillNotes = mode === 'skill';
  const showModelNotes = !!modelNotes;

  const toggleSection = (section: NoteType) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter((s) => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };

  if (!showSkillNotes && !showModelNotes) return null;

  return (
    <div className="mt-6 space-y-2">
      <div className="text-lg font-medium mb-3 text-gray-700 dark:text-gray-200">
        Additional Information
      </div>

      {/* Model-specific Notes */}
      {showModelNotes && (
        <NoteItem
          title={modelNotes?.title || `About ${model.label} Benchmarks`}
          type="info"
          expanded={expandedSections.includes('info')}
          onToggle={() => toggleSection('info')}
        >
          <div dangerouslySetInnerHTML={{ __html: modelNotes!.content }} />
        </NoteItem>
      )}

      {/* Skill Score Notes */}
      {showSkillNotes && (
        <NoteItem
          title="About Skill Scores"
          type="skill"
          expanded={expandedSections.includes('skill')}
          onToggle={() => toggleSection('skill')}
        >
          <h4 className="font-medium mb-2 text-gray-700 dark:text-gray-300">
            How skill scores are calculated
          </h4>
          <p className="mb-2">
            Skill scores measure the relative performance of a model compared to a reference model.
            They are calculated as:
          </p>
          <pre className="p-2 bg-gray-100 dark:bg-gray-800 rounded mb-2 overflow-x-auto">
            Skill Score = 100% × (1 - model_error/reference_model_error)
          </pre>
          <ul className="list-disc pl-5 space-y-1 mb-2">
            <li>
              A <strong>positive</strong> score indicates <strong>better</strong> performance than
              the reference model
            </li>
            <li>
              A <strong>negative</strong> score indicates <strong>worse</strong> performance than
              the reference model
            </li>
            <li>
              A score of <strong>0%</strong> indicates equal performance
            </li>
          </ul>
          <p className="mb-2">
            <strong>Note:</strong> Skill scores are only shown for time points where the reference
            model has data. If the reference model has a different time resolution (e.g., 6-hourly),
            skill scores will only be calculated at those time points.
          </p>
        </NoteItem>
      )}

      {/* Missing Data Note */}
      <NoteItem
        title="About Missing Data"
        type="missing"
        expanded={expandedSections.includes('missing')}
        onToggle={() => toggleSection('missing')}
      >
        <p className="mb-2">
          Some models may not have data for all parameters or time points. These models will not
          appear on the chart for those specific parameters or time points.
        </p>
        <p>
          If a model is missing from the chart when you expected to see it, it may not have data for
          the currently selected parameter or time range.
        </p>
      </NoteItem>
    </div>
  );
}
